import type { PreOrder, PreOrderFlow } from "@/app/api/types/preOrder";
import { PreOrderType, PreOrderStatus } from "@/app/api/types/preOrder";
import { PreOrderFlowStatus } from "@/app/api/types/preOrderFlow";
import type { ProductResponse as Product } from "@/app/api/types/product";
import type { UnitResponse } from "@/app/api/types/unit";
import {
  calculateFlowDisplayWeight,
  calculateFlowTotal,
} from "./calculationService";
import type { RankedSearchConfig } from "@stockline/product-search";
import type {
  StatusFilter,
  DocumentTypeFilter,
  FilterCounts,
  SortOption,
  ViewMode,
  AdvancedDateFieldKey,
  AdvancedDateFieldState,
  AdvancedDateFilterState,
} from "../types";
import {
  extractZonedDateStr,
  zonedFieldsToUTCISOString,
  endOfDayZonedFieldsToUTCISOString,
} from "@/lib/datetime/companyTimezone";
import { getLastModifiedAt, apiInstantMs } from "../utils/lastModifiedAt";
import { productDisplayName } from "@/hooks/use-product-display-name";
import { latestPending, readBlocks } from "./aiResolveLocally/helpers";
import {
  NO_EMPLOYEE_FILTER_ID,
  NO_LOCATION_FILTER_ID,
} from "../types/filter.types";
import { isProposalPreOrder } from "../types/proposal.types";

/**
 * Proposal-priority comparator: pending proposals (create or update annotated)
 * sort first within each group so they're not buried under existing pre-orders.
 */
function comparePreOrderProposalPriority(a: PreOrder, b: PreOrder): number {
  const aP = isProposalPreOrder(a) ? 0 : 1;
  const bP = isProposalPreOrder(b) ? 0 : 1;
  return aP - bP;
}

// --- Recap-specific Fuse.js options and search configs ---

export const PARTNER_GROUP_FUSE_OPTIONS = {
  keys: [
    { name: "partnerName", weight: 2 },
    { name: "partner.usage_name", weight: 1.5 },
    { name: "partner.code", weight: 1.5 },
    { name: "preOrders.pre_order_number", weight: 1.5 },
    { name: "preOrders.customer_po_number", weight: 1.5 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

export interface GroupedByPartner {
  partnerId: string;
  partnerName: string;
  partner: PreOrder["partner"];
  preOrders: PreOrder[];
  sourceNames: string;
}

export interface GroupedByProduct {
  productId: string;
  product: Product | null;
  /**
   * Pre-order IDs (within the active scope) that contain at least one flow
   * for this product. The leaf `<ProductGroup>` uses this to scope its own
   * Dexie subscription via `usePreOrderFlows({ productId, preOrderIds })`
   * — no flows are materialized at the recap level anymore.
   */
  preOrderIds: string[];
  /**
   * Flow-time aggregates, computed from the v32
   * `[preOrderId+productId+updatedAt]` / `[preOrderId+productId+createdAt]`
   * compound indexes by `useProductBreakdownForPreOrders`. Drive the
   * `latest-updated` / `oldest-updated` / `latest-created` /
   * `oldest-created` sort comparators without ever loading a flow row at
   * the recap level. Null when no in-scope flow for this product carries
   * the corresponding timestamp.
   */
  latestUpdatedAt: string | null;
  oldestUpdatedAt: string | null;
  latestCreatedAt: string | null;
  oldestCreatedAt: string | null;
}

export interface RecapStats {
  ordersCount: number;
  partnersCount: number;
  flowsCount: number;
  /**
   * Tonnage — total display weight (kg) summed across EVERY flow of the
   * pre-orders in the set. Derived from the exact same array as
   * `ordersCount` / `partnersCount`, so the header CA/tonnage always reflect
   * the same date range + filters as the order/partner counts (STK-303).
   */
  totalWeight: number;
  /**
   * CA — total committed-price revenue across the same set. Uses committed
   * `price_per_weight` only (no phantom PriceCell preview), matching the PDF
   * recap and giving a stable business number.
   */
  totalAmount: number;
}

/**
 * A pre-order is "cancelled" when its status is CANCELLED (6) OR any flow
 * has been cancelled (status 3). Lets the recap surface partial cancellations.
 */
export function isCancelledPreOrder(po: PreOrder): boolean {
  if (po.status === 6) return true;
  return Array.isArray(po.flows) && po.flows.some((f) => f.status === 3);
}

export function isPendingAiProposalPreOrder(po: PreOrder): boolean {
  return isProposalPreOrder(po) && po.__proposal?.batch?.status === "pending";
}

/**
 * A pre-order is modifiable only while PENDING (0) or a whole-PO AI proposal
 * (AI_PROPOSAL, 8). Every other status — CONFIRMED, EXPORTED, RESERVED, SENT,
 * PAID, CANCELLED, EXPORT_FAILED — is terminal/locked: the header form, the
 * partner picker, the per-flow cells and the delete action must all render
 * read-only.
 *
 * CONFIRMED is intentionally NOT modifiable by this predicate. Confirmed
 * pre-orders still expose an explicit modify-mode for the ERP re-export
 * workflow, but that path is gated separately inside the flows table — no
 * field is editable in the default (non-modify-mode) state, which is exactly
 * what this predicate returns.
 *
 * A `deleted_at` timestamp also locks the pre-order regardless of status.
 */
export function isPreOrderModifiable(
  po: Pick<PreOrder, "status" | "deleted_at">,
): boolean {
  if (po.deleted_at != null) return false;
  return (
    po.status === PreOrderStatus.PENDING ||
    po.status === PreOrderStatus.AI_PROPOSAL
  );
}

/**
 * Re-exported here as constants so callers don't have to know which enum
 * each lives on. Numeric values: `AI_PROPOSAL_PREORDER_STATUS = 8`,
 * `AI_PROPOSAL_FLOW_STATUS = 5`.
 */
export const AI_PROPOSAL_PREORDER_STATUS = PreOrderStatus.AI_PROPOSAL;
export const AI_PROPOSAL_FLOW_STATUS = PreOrderFlowStatus.AI_PROPOSAL;

/**
 * True when the LATEST well-formed `ai_suggestions[]` entry has
 * status="pending" (legacy single blocks are wrapped by the reader).
 * Position matters: older pending entries (audit traces from a prior AI
 * re-proposal) don't count — only the most recent intent gates the
 * "needs review" UI signals.
 *
 * Delegates to the resolver's strict `readBlocks` so the predicate that
 * OFFERS accept/decline and the resolver that EXECUTES them share one
 * definition of "pending AI" — a laxer predicate here used to offer
 * accept buttons on malformed blocks the resolver then silently
 * skipped. Blocks failing validation are dropped with a logged warning
 * (see `warnDroppedBlocks` in the helpers), never silently.
 */
function metadataHasPendingAi(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return latestPending(readBlocks(metadata)) !== null;
}

export function flowHasAiSuggestion(flow: PreOrderFlow): boolean {
  return metadataHasPendingAi(flow.flow_metadata ?? null);
}

/** True when the pre-order header itself carries a pending ai_suggestions entry. */
export function headerHasPendingAi(po: PreOrder): boolean {
  const meta = (
    po as {
      pre_order_metadata?: Record<string, unknown> | null;
    }
  ).pre_order_metadata;
  return metadataHasPendingAi(meta);
}

/**
 * Pending header AI suggestion entry — the LAST `ai_suggestions[]` block
 * (or legacy single block) when its status is `"pending"`. Used by the
 * recap header banner to render the diff. Returns null when no pending
 * header suggestion exists.
 */
export function getLatestPendingHeaderSuggestion(po: PreOrder): {
  index: number;
  status: "pending";
  type: "add" | "modify" | "delete";
  changes?: Record<string, { from: unknown; to: unknown; finalTo?: unknown }>;
  batchId: string;
  proposedBy: string;
  proposedAt: string;
} | null {
  const meta = (po as { pre_order_metadata?: Record<string, unknown> | null })
    .pre_order_metadata;
  const pending = latestPending(readBlocks(meta));
  if (pending?.status !== "pending") return null;
  return { ...pending, status: "pending" };
}

/**
 * True when any V3 AI signal is attached to the PO: parent at AI_PROPOSAL,
 * header has a pending ai_suggestion block, OR any non-deleted flow has a
 * pending AI signal. Excludes the legacy `__proposal` annotation — those flow
 * through the separate proposal-batch path.
 *
 * Reads `po.flows` directly (not the per-PO Dexie liveFlows): AI proposals
 * originate from the backend AI service, never from the local modify buffer,
 * so the central recap pipeline is the authoritative source for path
 * enumeration. The modify buffer can't manufacture AI signals on flows it
 * doesn't yet know about.
 */
export function hasAnyPendingAi(po: PreOrder): boolean {
  if (po.status === AI_PROPOSAL_PREORDER_STATUS) return true;
  if (headerHasPendingAi(po)) return true;
  if (!Array.isArray(po.flows)) return false;
  return po.flows.some(
    (f) =>
      (f as { deleted_at?: string | null }).deleted_at == null &&
      flowHasAiSuggestion(f),
  );
}

/**
 * Path-mode targets for `/api/v2/ai-pre-orders/.../approve|decline`.
 * Returns every path with a pending AI signal: `"header"` when the PO
 * header carries a pending block, plus `"flows.<id>"` for each
 * non-deleted flow with a pending block. Every AI-touched mutation
 * lives as an explicit pending entry now, so row-status fallbacks are
 * no longer needed.
 */
/**
 * Discriminates an AI accept/decline by what it acted on: a whole AI-created
 * order (the order itself is the proposal, status AI_PROPOSAL) vs AI
 * modifications staged on an existing non-AI order. Drives the `order_kind`
 * dimension on `ai_proposal_accepted` / `ai_proposal_declined`.
 */
export function aiOrderKind(po: PreOrder): "ai_created" | "ai_modified" {
  return po.status === PreOrderStatus.AI_PROPOSAL
    ? "ai_created"
    : "ai_modified";
}

export function collectAiPaths(po: PreOrder): string[] {
  const paths: string[] = [];
  if (headerHasPendingAi(po)) paths.push("header");
  if (Array.isArray(po.flows)) {
    for (const f of po.flows) {
      if ((f as { deleted_at?: string | null }).deleted_at != null) continue;
      if (flowHasAiSuggestion(f)) paths.push(`flows.${f.id}`);
    }
  }
  return paths;
}

/**
 * A pre-order surfaces in the "AI Changes" filter when *any* AI suggestion
 * is attached to it: the header carries a pending `ai_suggestions[]`
 * entry, or any non-deleted flow does. The legacy `__proposal` batch
 * annotation is honored so the filter stays correct during the V3
 * migration.
 */
/**
 * Shape-agnostic core of {@link hasAiChanges}: a row carries an AI change when
 * it's a whole-PO AI proposal (status 8), or its header / any non-deleted flow
 * holds a pending `ai_suggestions[]` block. Takes raw parts (status + metadata
 * objects) so callers on EITHER pre-order shape can reuse the SAME rule — recap
 * (legacy snake_case `pre_order_metadata` / `flow_metadata`) and the home
 * "Pending AI modifications" pill (V2 camelCase) both feed this, keeping the
 * pill's count and recap's `ai_proposals` list definitionally identical.
 */
export function aiChangesFromParts(
  status: number,
  headerMetadata: Record<string, unknown> | null | undefined,
  flowMetadatas: ReadonlyArray<Record<string, unknown> | null | undefined>,
): boolean {
  return (
    status === AI_PROPOSAL_PREORDER_STATUS ||
    metadataHasPendingAi(headerMetadata) ||
    flowMetadatas.some((m) => metadataHasPendingAi(m))
  );
}

export function hasAiChanges(po: PreOrder): boolean {
  const flowMetadatas = Array.isArray(po.flows)
    ? po.flows
        .filter((f) => (f as { deleted_at?: string | null }).deleted_at == null)
        .map((f) => f.flow_metadata)
    : [];
  // The extra `isPendingAiProposalPreOrder` covers recap's virtual create
  // proposals (`__proposal` rows merged into the list); real rows never carry
  // it, so the shared core is enough for the home's V2 set.
  return (
    aiChangesFromParts(po.status, po.pre_order_metadata, flowMetadatas) ||
    isPendingAiProposalPreOrder(po)
  );
}

/**
 * "Créée par IA" — the AI owns the whole order (parent at AI_PROPOSAL).
 * Mutually exclusive with `hasAiModifications`: the two split `hasAiChanges`
 * into the status-filter buckets `ai_created` / `ai_modified`.
 */
export function isAiCreatedPreOrder(po: PreOrder): boolean {
  return po.status === AI_PROPOSAL_PREORDER_STATUS;
}

/**
 * "Modifiée par IA" — a pending AI signal (header / flow suggestion block,
 * or legacy proposal batch) on an order the AI does NOT own. AI-created
 * orders are excluded so each order lands in exactly one AI bucket.
 */
export function hasAiModifications(po: PreOrder): boolean {
  return !isAiCreatedPreOrder(po) && hasAiChanges(po);
}

// --- Advanced date filtering (STK-750) ---

/** Company-tz calendar day (yyyy-MM-dd) for a pre-order's value on one
 * dimension, or null when the dimension has no date. `delivery_date` is a
 * date-only column (already a calendar day); `placed`/`modified` are absolute
 * UTC datetimes resolved to the company-tz day so the filter buckets the same
 * way the columns display. */
function preOrderDayForField(
  po: PreOrder,
  key: AdvancedDateFieldKey,
  tz: string,
): string | null {
  if (key === "delivery") {
    return po.delivery_date ? po.delivery_date.slice(0, 10) : null;
  }
  if (key === "placed") {
    const src = po.order_date ?? po.created_at;
    return src ? extractZonedDateStr(src, tz) : null;
  }
  // "modified" — header⊕flows rollup.
  return extractZonedDateStr(getLastModifiedAt(po), tz);
}

/** Whether a calendar-day string satisfies one field's exact/range/from rule.
 * Used for `delivery` (date-only column) and for `exact` mode on any field. */
function dayMatchesField(day: string, field: AdvancedDateFieldState): boolean {
  if (field.rangeMode === "exact") return day === field.date;
  if (field.rangeMode === "range") {
    const upper =
      field.endDate && field.endDate >= field.date ? field.endDate : field.date;
    return day >= field.date && day <= upper;
  }
  // "starting_from"
  return day >= field.date;
}

// STK-790 default day-boundary times, mirroring simple mode (DateModeSelector).
const DEFAULT_START_TIME = "00:00";
const DEFAULT_END_TIME = "23:59";

/**
 * Browser-local midnight Date whose calendar fields (year/month/day) equal the
 * `yyyy-MM-dd` company-tz day — the exact shape `zonedFieldsToUTCISOString`
 * consumes (it reads `format(date, "yyyy-MM-dd")`). Mirrors `parseDateOnly`
 * but is inlined here on purpose: importing `dateUtils` would pull `@/lib/i18n`
 * into this module's graph and break the partial-`react-i18next`-mock tests
 * (see the header note in `lastModifiedAt.ts`).
 */
function dayStartLocalDate(ymd: string): Date {
  const [y, m, d] = ymd.split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** Raw source string backing a time-bearing dimension, or null when absent.
 * `placed` → `order_date` (fallback `created_at`); `modified` → the
 * header⊕flows last-modified rollup (`getLastModifiedAt`). This is the SAME
 * source `preOrderDayForField` reads for the day-compare path — kept as one
 * resolver so the instant and day paths never drift on which field backs
 * each dimension. */
function preOrderTimeSource(
  po: PreOrder,
  key: "placed" | "modified",
): string | null {
  if (key === "placed") return po.order_date ?? po.created_at ?? null;
  return getLastModifiedAt(po);
}

/** Whether a source string carries a time-of-day component. Date-only values
 * ("YYYY-MM-DD" — e.g. an AI virtual proposal's `order_date`, see
 * `utils/proposalBuilders.ts`) parse via `apiInstantMs` to a spurious UTC
 * midnight instant (NOT NaN), which would then be wrongly compared against
 * company-tz bounds. Mirrors `utils/dateRange.ts` `matchesOrder`'s guard. */
function hasTimeComponent(src: string): boolean {
  return src.includes("T") || /\d{4}-\d{2}-\d{2} \d{2}:/.test(src);
}

/** Absolute instant (ms) of a pre-order's time-bearing dimension, or null when
 * the dimension has no timestamp or the source is date-only (caller routes
 * date-only sources to the day-compare path instead). */
function preOrderInstantMs(
  po: PreOrder,
  key: "placed" | "modified",
): number | null {
  const src = preOrderTimeSource(po, key);
  if (!src || !hasTimeComponent(src)) return null;
  const ms = apiInstantMs(src);
  return Number.isNaN(ms) ? null : ms;
}

/** Lower/upper instant bounds (ms) for a time-bearing field in `range` /
 * `starting_from` mode, resolved in the company tz. Default times reproduce
 * the legacy whole-day range: 00:00 lower, inclusive end-of-day upper. */
function fieldInstantBounds(
  field: AdvancedDateFieldState,
  tz: string,
): { lowerMs: number; upperMs: number | null } {
  const startTime = field.startTime ?? DEFAULT_START_TIME;
  const lowerMs = new Date(
    zonedFieldsToUTCISOString(dayStartLocalDate(field.date), startTime, tz),
  ).getTime();

  if (field.rangeMode === "starting_from") return { lowerMs, upperMs: null };

  // "range"
  const endDay =
    field.endDate && field.endDate >= field.date ? field.endDate : field.date;
  const endDate = dayStartLocalDate(endDay);
  const endTime = field.endTime;
  // Default / "23:59" → true inclusive end-of-day (23:59:59.999) so an
  // untouched filter drops nothing it kept before (Constitution 2.1.8). Any
  // other explicit end time → HH:mm:00.
  const upperMs =
    !endTime || endTime === DEFAULT_END_TIME
      ? new Date(endOfDayZonedFieldsToUTCISOString(endDate, tz)).getTime()
      : new Date(zonedFieldsToUTCISOString(endDate, endTime, tz)).getTime();

  return { lowerMs, upperMs };
}

/** Whether one active field keeps this pre-order. `delivery` (no time column)
 * and `exact` mode always compare on the company-tz calendar day; `placed` /
 * `modified` in `range` / `starting_from` compare absolute instants (STK-790)
 * — UNLESS the resolved source is a date-only value (e.g. an AI virtual
 * proposal's bare `order_date`), which has no time-of-day to bound and falls
 * back to the same calendar-day compare so it isn't spuriously dropped by a
 * non-UTC company tz (see `hasTimeComponent`). */
function fieldMatchesPreOrder(
  po: PreOrder,
  key: AdvancedDateFieldKey,
  field: AdvancedDateFieldState,
  tz: string,
): boolean {
  if (key === "delivery" || field.rangeMode === "exact") {
    const day = preOrderDayForField(po, key, tz);
    return !!day && dayMatchesField(day, field);
  }
  const src = preOrderTimeSource(po, key);
  if (!src) return false;
  if (!hasTimeComponent(src)) {
    const day = preOrderDayForField(po, key, tz);
    return !!day && dayMatchesField(day, field);
  }
  const ms = preOrderInstantMs(po, key);
  if (ms == null) return false;
  const { lowerMs, upperMs } = fieldInstantBounds(field, tz);
  if (ms < lowerMs) return false;
  if (upperMs != null && ms > upperMs) return false;
  return true;
}

export const ADVANCED_DATE_FIELD_KEYS: readonly AdvancedDateFieldKey[] = [
  "delivery",
  "placed",
  "modified",
];

/**
 * OR-combined advanced date filter (STK-750; time-of-day STK-790). Returns
 * true (no-op) when disabled or no field is active; otherwise keeps a pre-order
 * that matches ANY active dimension. `delivery` and `exact` mode match on the
 * company-tz calendar day; `placed`/`modified` in `range`/`starting_from`
 * match on absolute instants so a time-of-day bound can enter.
 */
export function matchesAdvancedDateFilter(
  po: PreOrder,
  filter: AdvancedDateFilterState | undefined,
  tz: string,
): boolean {
  if (!filter?.enabled) return true;
  let anyActive = false;
  for (const key of ADVANCED_DATE_FIELD_KEYS) {
    const field = filter.fields[key];
    if (!field?.active) continue;
    anyActive = true;
    if (fieldMatchesPreOrder(po, key, field, tz)) return true;
  }
  return !anyActive;
}

export function filterPreOrders(
  preOrders: PreOrder[],
  statusFilter: StatusFilter,
  viewMode: ViewMode,
  channelFilter: number[] = [],
  documentTypeFilter: DocumentTypeFilter = [],
): PreOrder[] {
  if (!preOrders?.length) return [];

  let filtered = preOrders;

  if (documentTypeFilter.length > 0) {
    filtered = filtered.filter((po) => {
      if (
        documentTypeFilter.includes("orders") &&
        po.type === PreOrderType.PRE_ORDER
      )
        return true;
      if (
        documentTypeFilter.includes("avoirs") &&
        po.type === PreOrderType.AVOIR
      )
        return true;
      if (
        documentTypeFilter.includes("purchases") &&
        po.type === PreOrderType.PURCHASE
      )
        return true;
      return false;
    });
  }

  if (channelFilter.length > 0) {
    filtered = filtered.filter((po) => channelFilter.includes(po.source_type));
  }

  return filtered;
}

/**
 * Keep pre-orders whose employee is in the selected set. The sentinel
 * `NO_EMPLOYEE_FILTER_ID` matches pre-orders with no `employee_id` and may
 * sit alongside real UUIDs (union semantics).
 */
export function filterPreOrdersByEmployees(
  preOrders: PreOrder[],
  employees: string[],
): PreOrder[] {
  if (employees.length === 0) return preOrders;
  const includeUnassigned = employees.includes(NO_EMPLOYEE_FILTER_ID);
  return preOrders.filter((po) =>
    po.employee_id ? employees.includes(po.employee_id) : includeUnassigned,
  );
}

/**
 * Keep pre-orders whose location is in the selected set. The sentinel
 * `NO_LOCATION_FILTER_ID` matches pre-orders with no `location_id` and may
 * sit alongside real UUIDs (union semantics).
 */
export function filterPreOrdersByLocations(
  preOrders: PreOrder[],
  locations: string[],
): PreOrder[] {
  if (locations.length === 0) return preOrders;
  const includeUnassigned = locations.includes(NO_LOCATION_FILTER_ID);
  return preOrders.filter((po) =>
    po.location_id ? locations.includes(po.location_id) : includeUnassigned,
  );
}

function isNumericSKU(sku: string): boolean {
  return /^\d+$/.test(sku);
}

function compareSKUs(skuA: string, skuB: string): number {
  const aNum = isNumericSKU(skuA);
  const bNum = isNumericSKU(skuB);
  if (aNum && bNum) return parseInt(skuA, 10) - parseInt(skuB, 10);
  if (!aNum && !bNum) return skuA.localeCompare(skuB);
  return aNum ? -1 : 1;
}

export const PARTNER_SEARCH_CONFIG: RankedSearchConfig<GroupedByPartner> = {
  codeField: (item) => item.partner?.code,
  textFields: (item) => [
    item.partnerName,
    item.partner?.usage_name,
    item.partner?.code,
    ...item.preOrders.flatMap((po) => [
      po.pre_order_number,
      po.customer_po_number,
    ]),
  ],
  fuseOptions: PARTNER_GROUP_FUSE_OPTIONS,
};

/**
 * Build partner groups from pre-orders (no search, no sort).
 * Pure grouping — one card per pre-order, sorted by created_at desc.
 * Stable output for a given input, safe to memoize.
 *
 * Dedups by pre-order id before grouping. Each card's React key is the
 * pre-order id, so duplicates would corrupt the virtualized list (stuck
 * expansion, overlapping rows). Dedup is defensive — sources that can
 * leak duplicates today: AI batches with multiple "create" proposals
 * share `proposal-${batch.id}` as virtual id (see buildVirtualPreOrder),
 * and any future merge bug would surface as a stuck recap row.
 */
export function buildPartnerGroups(
  preOrders: PreOrder[],
  statusFilter: StatusFilter,
  viewMode: ViewMode,
  t: (key: string) => string,
): GroupedByPartner[] {
  let filtered = preOrders;
  if (viewMode === "per-client" && statusFilter !== "all") {
    filtered = preOrders.filter((po) => {
      if (statusFilter === "pending") return po.status === 0;
      if (statusFilter === "confirmed") return po.status === 1;
      if (statusFilter === "exported") return po.status === 2;
      if (statusFilter === "cancelled") return isCancelledPreOrder(po);
      if (statusFilter === "ai_proposals") return hasAiChanges(po);
      return true;
    });
  }

  const seenIds = new Set<string>();
  const deduped: PreOrder[] = [];
  for (const po of filtered) {
    if (seenIds.has(po.id)) {
      // Warn in prod too — duplicates indicate an upstream data bug
      // (e.g. AI batch with multiple "create" proposals sharing
      // proposal-${batch.id} as virtual id) that monitoring should catch.
      // eslint-disable-next-line no-console
      console.warn(
        "[buildPartnerGroups] duplicate pre-order id dropped — fix the upstream source",
        po.id,
      );
      continue;
    }
    seenIds.add(po.id);
    deduped.push(po);
  }

  const sorted = deduped.sort((a, b) => {
    const proposalCmp = comparePreOrderProposalPriority(a, b);
    if (proposalCmp !== 0) return proposalCmp;
    return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
  });

  return sorted.map((po) => {
    const partnerId = po.partner_id || `no-partner-${po.id}`;
    const partner = po.partner;

    let sourceName = "";
    if (po.source_type === 1 && po.whatsapp_chat) {
      sourceName =
        po.whatsapp_chat.name || po.whatsapp_chat.display_name || "WhatsApp";
    } else if (po.source_type === 5 && po.sms_chat) {
      sourceName = po.sms_chat.display_name || po.sms_chat.phone_number;
    } else if (po.source_type === 0 && po.email_contact) {
      sourceName =
        po.email_contact.display_name || po.email_contact.email_address;
    } else if (po.source_type === 4) {
      sourceName = "Internal Offer";
    }

    return {
      partnerId,
      partnerName: partner?.name || t("ordersRecap.noPartner"),
      partner,
      preOrders: [po],
      sourceNames: sourceName,
    };
  });
}

/** Pending proposals first within their partner group, regardless of sort. */
function comparePartnerGroupProposalPriority(
  a: GroupedByPartner,
  b: GroupedByPartner,
): number {
  const aP = a.preOrders.some(isProposalPreOrder) ? 0 : 1;
  const bP = b.preOrders.some(isProposalPreOrder) ? 0 : 1;
  return aP - bP;
}

export function getPartnerSortComparator(
  sortOption: SortOption,
): (a: GroupedByPartner, b: GroupedByPartner) => number {
  const inner = ((): typeof base | undefined => {
    type C = (a: GroupedByPartner, b: GroupedByPartner) => number;
    const base: C | undefined = undefined;
    switch (sortOption) {
      case "alphabetical":
        return ((a, b) => a.partnerName.localeCompare(b.partnerName)) as C;
      case "latest-updated":
      case "oldest-updated": {
        const desc = sortOption === "latest-updated";
        return ((a, b) => {
          const pick = desc ? Math.max : Math.min;
          const aD = pick(
            ...a.preOrders.map((po) =>
              new Date(po.updated_at || po.created_at).getTime(),
            ),
          );
          const bD = pick(
            ...b.preOrders.map((po) =>
              new Date(po.updated_at || po.created_at).getTime(),
            ),
          );
          return desc ? bD - aD : aD - bD;
        }) as C;
      }
      case "latest-created":
      case "oldest-created": {
        const desc = sortOption === "latest-created";
        return ((a, b) => {
          const pick = desc ? Math.max : Math.min;
          const aD = pick(
            ...a.preOrders.map((po) =>
              new Date(po.order_date || po.created_at).getTime(),
            ),
          );
          const bD = pick(
            ...b.preOrders.map((po) =>
              new Date(po.order_date || po.created_at).getTime(),
            ),
          );
          return desc ? bD - aD : aD - bD;
        }) as C;
      }
      default:
        return base;
    }
  })();

  // Always layer proposal-priority first, regardless of user sort selection.
  return (a, b) => {
    const proposalCmp = comparePartnerGroupProposalPriority(a, b);
    if (proposalCmp !== 0) return proposalCmp;
    return inner ? inner(a, b) : 0;
  };
}

export function applySortToPartnerGroups(
  groups: GroupedByPartner[],
  sortOption: SortOption,
): GroupedByPartner[] {
  const comparator = getPartnerSortComparator(sortOption);
  return [...groups].sort(comparator);
}

// --- Partner grouping (divider items for virtualized list) ---

export type PartnerListItem =
  | {
      type: "divider";
      partnerName: string;
      partnerCode: string | undefined;
      count: number;
    }
  | { type: "card"; group: GroupedByPartner };

/**
 * Takes the flat list of per-pre-order partner groups and clusters them
 * by partner, inserting divider items between each partner section.
 *
 * When `preserveInputOrder` is false (default), buckets are sorted at
 * the section level by `sortOption`. When true (search is active), the
 * input already carries tier-ranked ordering from rankedFuzzyFilter, so
 * bucket order follows first-appearance in the input instead.
 */
export function clusterByPartner(
  groups: GroupedByPartner[],
  sortOption: SortOption,
  preserveInputOrder = false,
): PartnerListItem[] {
  if (!groups.length) return [];

  // Bucket cards by partnerId (Map preserves insertion order)
  const buckets = new Map<string, GroupedByPartner[]>();
  for (const g of groups) {
    const key = g.partnerId;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(g);
    else buckets.set(key, [g]);
  }

  // Sort buckets — skip when input is already search-ranked
  let sortedBuckets = Array.from(buckets.values());
  if (!preserveInputOrder) {
    sortedBuckets = applySortToBuckets(sortedBuckets, sortOption);
  }

  // Flatten into divider + card items
  const result: PartnerListItem[] = [];
  for (const bucket of sortedBuckets) {
    const first = bucket[0];
    result.push({
      type: "divider",
      partnerName: first.partnerName,
      partnerCode: first.partner?.code ?? undefined,
      count: bucket.length,
    });
    for (const g of bucket) {
      result.push({ type: "card", group: g });
    }
  }
  return result;
}

function applySortToBuckets(
  buckets: GroupedByPartner[][],
  sortOption: SortOption,
): GroupedByPartner[][] {
  switch (sortOption) {
    case "alphabetical":
      return buckets.sort((a, b) =>
        a[0].partnerName.localeCompare(b[0].partnerName),
      );
    case "latest-updated":
    case "oldest-updated": {
      const desc = sortOption === "latest-updated";
      return buckets.sort((a, b) => {
        const pick = desc ? Math.max : Math.min;
        const aD = pick(
          ...a.flatMap((g) =>
            g.preOrders.map((po) =>
              new Date(po.updated_at || po.created_at).getTime(),
            ),
          ),
        );
        const bD = pick(
          ...b.flatMap((g) =>
            g.preOrders.map((po) =>
              new Date(po.updated_at || po.created_at).getTime(),
            ),
          ),
        );
        return desc ? bD - aD : aD - bD;
      });
    }
    case "latest-created":
    case "oldest-created": {
      const desc = sortOption === "latest-created";
      return buckets.sort((a, b) => {
        const pick = desc ? Math.max : Math.min;
        const aD = pick(
          ...a.flatMap((g) =>
            g.preOrders.map((po) =>
              new Date(po.order_date || po.created_at).getTime(),
            ),
          ),
        );
        const bD = pick(
          ...b.flatMap((g) =>
            g.preOrders.map((po) =>
              new Date(po.order_date || po.created_at).getTime(),
            ),
          ),
        );
        return desc ? bD - aD : aD - bD;
      });
    }
    default:
      return buckets;
  }
}

// --- Product search config ---

export const PRODUCT_GROUP_FUSE_OPTIONS = {
  keys: [
    { name: "product.name", weight: 2 },
    { name: "product.short_name", weight: 1.5 },
    { name: "product.sku", weight: 1.5 },
    { name: "product.code", weight: 1 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

export const PRODUCT_SEARCH_CONFIG: RankedSearchConfig<GroupedByProduct> = {
  codeField: (item) => item.product?.sku,
  textFields: (item) => [
    item.product?.name,
    item.product?.short_name,
    item.product?.sku,
    item.product?.code,
  ],
  fuseOptions: PRODUCT_GROUP_FUSE_OPTIONS,
};

/**
 * Build lean product groups from a pre-fetched product breakdown.
 *
 * `breakdown` comes from `useProductBreakdownForPreOrders(visiblePoIds)` —
 * one `(productId, preOrderIds[])` entry per product appearing in any flow
 * of a visible PO. We pair each `productId` with a `Product` from the
 * passed-in lookup (the recap-wide products cache) and return a shape that
 * the `<ProductGroup>` leaf can consume directly to scope its own per-
 * product Dexie subscription. No flow rows are materialised here — that
 * was the whole point of the v31 compound index.
 *
 * Status filtering: PO-level filters (`exported`, `cancelled`,
 * `ai_proposals`) are applied upstream when building the visible PO set, so
 * the breakdown already excludes the wrong rows. Flow-level filters
 * (`pending`, `confirmed`) can't be enforced at this layer without a flow
 * scan — each `<ProductGroup>` filters its own subscription by
 * `flow.status` and self-hides when the result is empty.
 */
export function buildProductGroups(
  breakdown: ReadonlyArray<{
    productId: string;
    preOrderIds: string[];
    latestUpdatedAt: string | null;
    oldestUpdatedAt: string | null;
    latestCreatedAt: string | null;
    oldestCreatedAt: string | null;
  }>,
  productsById: Map<string, Product>,
): GroupedByProduct[] {
  const groups: GroupedByProduct[] = [];
  for (const entry of breakdown) {
    const product = productsById.get(entry.productId) ?? null;
    groups.push({
      productId: entry.productId,
      product,
      preOrderIds: entry.preOrderIds,
      latestUpdatedAt: entry.latestUpdatedAt,
      oldestUpdatedAt: entry.oldestUpdatedAt,
      latestCreatedAt: entry.latestCreatedAt,
      oldestCreatedAt: entry.oldestCreatedAt,
    });
  }
  return groups;
}

export function getProductSortComparator(
  sortOption: SortOption,
  /**
   * Mirrors the `showProductShortName` flag. Alphabetical order has to key on
   * the label actually rendered — sorting by the short name while the full
   * name is on screen makes a correctly sorted list look unsorted.
   */
  preferShortName = false,
): ((a: GroupedByProduct, b: GroupedByProduct) => number) | undefined {
  switch (sortOption) {
    case "code":
      return (a, b) => compareSKUs(a.product?.sku || "", b.product?.sku || "");
    case "alphabetical":
      return (a, b) => {
        const nA = productDisplayName(a.product, preferShortName);
        const nB = productDisplayName(b.product, preferShortName);
        return nA.localeCompare(nB);
      };
    case "latest-updated":
      // Sort by max(flow.updatedAt) per product, descending. Timestamps
      // come from `useProductBreakdownForPreOrders` walking the v32
      // `[preOrderId+productId+updatedAt]` compound index; nulls (no
      // in-scope flow with this timestamp) sort to the bottom in every
      // direction so they don't bubble to the top of a "most recent" view.
      return (a, b) =>
        compareTimestampsDesc(a.latestUpdatedAt, b.latestUpdatedAt);
    case "oldest-updated":
      return (a, b) =>
        compareTimestampsAsc(a.oldestUpdatedAt, b.oldestUpdatedAt);
    case "latest-created":
      return (a, b) =>
        compareTimestampsDesc(a.latestCreatedAt, b.latestCreatedAt);
    case "oldest-created":
      return (a, b) =>
        compareTimestampsAsc(a.oldestCreatedAt, b.oldestCreatedAt);
    default:
      return undefined;
  }
}

// ISO-8601 strings sort lexicographically the same way they sort
// chronologically, so we compare strings directly — avoids the `new Date`
// allocation per call that the old flow-iterating comparator paid.
function compareTimestampsDesc(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a == null) return 1; // nulls last
  if (b == null) return -1;
  return a < b ? 1 : -1;
}

function compareTimestampsAsc(a: string | null, b: string | null): number {
  if (a === b) return 0;
  if (a == null) return 1; // nulls last
  if (b == null) return -1;
  return a < b ? -1 : 1;
}

export function applySortToProductGroups(
  groups: GroupedByProduct[],
  sortOption: SortOption,
  preferShortName = false,
): GroupedByProduct[] {
  const comparator = getProductSortComparator(sortOption, preferShortName);
  return comparator ? [...groups].sort(comparator) : groups;
}

export function calculateStats(
  preOrders: PreOrder[],
  units?: UnitResponse[],
): RecapStats {
  const uniquePartners = new Set(
    preOrders.map((po) => po.partner_id).filter(Boolean),
  ).size;
  // Single pass over the same pre-orders that drive ordersCount/partnersCount,
  // accumulating tonnage + CA so the header KPIs share one date/filter scope.
  let totalFlows = 0;
  let totalWeight = 0;
  let totalAmount = 0;
  for (const po of preOrders) {
    const flows = po.flows ?? [];
    totalFlows += flows.length;
    for (const flow of flows) {
      totalWeight += calculateFlowDisplayWeight(flow, units);
      totalAmount += calculateFlowTotal(flow, units);
    }
  }
  return {
    ordersCount: preOrders.length,
    partnersCount: uniquePartners,
    flowsCount: totalFlows,
    totalWeight: Math.round(totalWeight * 100) / 100,
    totalAmount: Math.round(totalAmount * 100) / 100,
  };
}

export function calculateFilterCounts(
  preOrders: PreOrder[] | undefined,
  documentTypeFilter: DocumentTypeFilter = [],
): FilterCounts {
  if (!preOrders?.length)
    return {
      all: 0,
      pending: 0,
      confirmed: 0,
      exported: 0,
      cancelled: 0,
      export_failed: 0,
      ai_created: 0,
      ai_modified: 0,
      deleted: 0,
    };

  let filtered = preOrders;
  if (documentTypeFilter.length > 0) {
    filtered = filtered.filter((po) => {
      if (
        documentTypeFilter.includes("orders") &&
        po.type === PreOrderType.PRE_ORDER
      )
        return true;
      if (
        documentTypeFilter.includes("avoirs") &&
        po.type === PreOrderType.AVOIR
      )
        return true;
      if (
        documentTypeFilter.includes("purchases") &&
        po.type === PreOrderType.PURCHASE
      )
        return true;
      return false;
    });
  }

  return {
    all: filtered.length,
    pending: filtered.filter((po) => po.status === 0).length,
    confirmed: filtered.filter((po) => po.status === 1).length,
    exported: filtered.filter((po) => po.status === 2).length,
    cancelled: filtered.filter(isCancelledPreOrder).length,
    export_failed: filtered.filter(
      (po) => po.pre_order_metadata?.export?.status === "failed",
    ).length,
    ai_created: filtered.filter(isAiCreatedPreOrder).length,
    ai_modified: filtered.filter(hasAiModifications).length,
    deleted: filtered.filter((po) => po.deleted_at != null).length,
  };
}

export function getDocumentTypeCounts(preOrders: PreOrder[]) {
  if (!preOrders?.length) return { orders: 0, avoirs: 0, purchases: 0 };
  return {
    orders: preOrders.filter((po) => po.type === PreOrderType.PRE_ORDER).length,
    avoirs: preOrders.filter((po) => po.type === PreOrderType.AVOIR).length,
    purchases: preOrders.filter((po) => po.type === PreOrderType.PURCHASE)
      .length,
  };
}
